import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Download, AlertCircle, Check, X, Volume2, Image as ImageIcon, Film, Share2 } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';
import { showErrorToast, showSuccessToast, sanitizeError } from '../../utils/safeToast';

const STATUS_LABELS = {
  analyzing: 'Bild wird analysiert...',
  prompting: 'Prompt wird erstellt...',
  generating: 'Video wird generiert...',
  completed: 'Fertig!',
  failed: 'Fehlgeschlagen'
};

const STATUS_ICONS = { analyzing: Loader2, prompting: Loader2, generating: Film, completed: Check, failed: AlertCircle };
const PIPELINE_STEPS = ['analyzing', 'prompting', 'generating', 'completed'];
const MAX_TEXT_LENGTH = 5000;

const LipSyncPanel = ({ avatarId, generations = [] }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [text, setText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [error, setError] = useState(null);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const pollingRef = useRef(null);

  const startPolling = useCallback((jobId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get(`/api/lipsync/${jobId}`);
        if (res.ok) {
          const data = await res.json();
          setCurrentJob(data);
          if (data.status === 'completed') {
            clearInterval(pollingRef.current); pollingRef.current = null;
            setIsGenerating(false);
            showSuccessToast('Video fertig!');
            // Galerie-Refresh triggern
            window.dispatchEvent(new CustomEvent('lipsyncVideoCompleted'));
          } else if (data.status === 'failed') {
            clearInterval(pollingRef.current); pollingRef.current = null;
            setIsGenerating(false);
            setError(sanitizeError(data.error || 'Fehlgeschlagen'));
          }
        }
      } catch (e) { /* ignore */ }
    }, 3000);
  }, []);

  useEffect(() => { return () => { if (pollingRef.current) clearInterval(pollingRef.current); }; }, []);

  const handleGenerate = async () => {
    if (!selectedImage || !text.trim()) return;
    setIsGenerating(true); setError(null); setCurrentJob(null);

    try {
      let aspectRatio = '16:9';
      try {
        const img = new Image();
        await new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; img.src = selectedImage.url; });
        if (img.width && img.height) aspectRatio = img.height > img.width * 1.2 ? '9:16' : '16:9';
      } catch (e) { /* Default 16:9 */ }

      const res = await apiClient.post('/api/video-pipeline/generate', {
        generation_id: selectedImage.generationId,
        image_index: selectedImage.imageIndex || 0,
        text: text.trim(),
        aspect_ratio: aspectRatio,
      });

      if (!res.ok) {
        const errData = await res.json();
        const detail = errData?.detail || 'Fehlgeschlagen';
        if (res.status === 402 || detail.toLowerCase().includes('snap')) {
          setError('Nicht genug SNAPs.'); showErrorToast('Nicht genug SNAPs.');
        } else {
          setError(sanitizeError(typeof detail === 'string' ? detail : 'Fehlgeschlagen'));
        }
        setIsGenerating(false); return;
      }

      const data = await res.json();
      setCurrentJob(data);
      startPolling(data.id || data.lipsync_id);
    } catch (err) {
      console.error('Fehler:', err);
      setError('Verbindungsfehler. Bitte versuche es erneut.');
      setIsGenerating(false);
    }
  };

  const handleSelectImage = (gen, imgUrl, imgIndex) => {
    setSelectedImage({ url: imgUrl, generationId: gen.id || gen._id, imageIndex: imgIndex });
    setShowImagePicker(false);
  };

  const allImages = [];
  (generations || []).forEach(gen => {
    const urls = gen.image_urls || (gen.image_url ? [gen.image_url] : []);
    urls.forEach((url, idx) => { if (url && url.trim()) allImages.push({ gen, url, index: idx }); });
  });

  const userCredits = (() => { try { return JSON.parse(localStorage.getItem('snapshot_user') || '{}').credits || 0; } catch { return 0; } })();
  const hasEnoughCredits = userCredits >= 1;

  const getStatusLabel = () => {
    if (!currentJob) return '';
    return currentJob.status_message || STATUS_LABELS[currentJob.status || currentJob.pipeline_step] || 'Wird verarbeitet...';
  };
  const currentStepIndex = currentJob ? PIPELINE_STEPS.indexOf(currentJob.status || currentJob.pipeline_step || 'analyzing') : -1;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 backdrop-blur border border-indigo-500/30 rounded-2xl flex items-center justify-center">
          <Film className="w-6 h-6 text-indigo-400 drop-shadow-[0_0_8px_rgba(99,102,241,0.6)]" />
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">Sprechender Avatar</h2>
          <p className="text-white/50 text-sm">Bild auswählen, Text eingeben — fertig.</p>
        </div>
      </div>

      {/* BILD */}
      <div>
        <h3 className="text-white font-bold text-sm mb-3 uppercase tracking-wider flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-indigo-400" /> Bild auswählen
        </h3>
        {selectedImage ? (
          <div className="relative inline-block">
            <div className="relative rounded-xl overflow-hidden border-2 border-indigo-500/50 max-w-[200px]">
              <img src={selectedImage.url} alt="Ausgewählt" className="w-full h-auto" />
              <div className="absolute top-2 left-2 px-2 py-1 bg-indigo-500 text-white text-xs font-bold rounded-lg shadow-lg">Ausgewählt</div>
            </div>
            <button onClick={() => setSelectedImage(null)} className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all shadow-lg"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <button onClick={() => setShowImagePicker(true)} disabled={isGenerating} className="w-full py-6 border-2 border-dashed border-white/20 hover:border-indigo-500/50 rounded-xl text-white/50 hover:text-white transition-all flex flex-col items-center gap-2">
            <ImageIcon className="w-8 h-8" /><span className="text-sm font-medium">Bild aus Galerie wählen</span>
          </button>
        )}
      </div>

      {/* BILD-PICKER */}
      {showImagePicker && createPortal(
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 999999, background: 'rgba(0,0,0,0.92)' }} onClick={(e) => { if (e.target === e.currentTarget) setShowImagePicker(false); }}>
          <div className="relative w-full max-h-[85vh] bg-[#0a0a0a] border-2 border-indigo-500/30 rounded-2xl overflow-hidden shadow-2xl" style={{ maxWidth: '720px' }}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-white font-bold text-lg">Bild auswählen</h3>
              <button onClick={() => setShowImagePicker(false)} className="w-9 h-9 flex items-center justify-center rounded-xl border-2 border-white/20 text-white hover:bg-white/10 transition-all"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 70px)' }}>
              {allImages.length === 0 ? (
                <div className="text-center py-12"><ImageIcon className="w-12 h-12 text-white/20 mx-auto mb-3" /><p className="text-white/40">Noch keine Bilder vorhanden</p></div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {allImages.map(({ gen, url, index }, i) => (
                    <button key={`${gen.id || gen._id}_${index}`} onClick={() => handleSelectImage(gen, url, index)}
                      className={`relative rounded-xl overflow-hidden border-2 transition-all group bg-white/5 w-full active:scale-95 ${selectedImage?.url === url ? 'border-indigo-500 ring-2 ring-indigo-500/50' : 'border-white/10 hover:border-indigo-500/50'}`}
                      style={{ aspectRatio: '3/4' }}>
                      <img src={url} alt={`Bild ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" loading="eager" draggable={false} onError={(e) => { e.target.style.display = 'none'; }} />
                      <div className={`absolute inset-0 transition-all flex items-center justify-center ${selectedImage?.url === url ? 'bg-indigo-500/30' : 'bg-black/0 group-hover:bg-black/30'}`}>
                        <Check className={`w-10 h-10 text-white drop-shadow-lg transition-all ${selectedImage?.url === url ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* TEXT */}
      <div>
        <h3 className="text-white font-bold text-sm mb-2 uppercase tracking-wider flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-indigo-400" /> Was soll dein Avatar tun?
        </h3>
        <div className="relative">
          <textarea value={text} onChange={(e) => { if (e.target.value.length <= MAX_TEXT_LENGTH) setText(e.target.value); }}
            placeholder={"Beschreibe die Szene oder was dein Avatar sagen soll.\n\nBeispiele:\n- \"Strandszene, die Frau stellt sich vor\"\n- Die Frau sagt: \"Hallo, willkommen!\"\n- \"Professionelles Vorstellungsvideo\""}
            className="w-full bg-white/5 border-2 border-white/20 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 rounded-xl p-4 text-white placeholder-white/30 text-sm transition-all resize-none" rows={4} disabled={isGenerating} />
          <div className="absolute bottom-3 right-3"><span className={`text-xs ${text.length >= MAX_TEXT_LENGTH ? 'text-red-400' : 'text-white/30'}`}>{text.length}/{MAX_TEXT_LENGTH}</span></div>
        </div>
        <p className="text-white/30 text-xs mt-1.5">KI erkennt automatisch ob du eine Szene beschreibst oder einen Sprechtext vorgibst.</p>
      </div>

      {/* ERROR */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div><p className="text-red-400 font-medium">Fehler</p><p className="text-red-300/80 text-sm">{error}</p></div>
          <button onClick={() => setError(null)} className="ml-auto text-red-400/60 hover:text-red-400"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* PROGRESS */}
      {isGenerating && currentJob && (
        <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
            <p className="text-white font-medium">{getStatusLabel()}</p>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {PIPELINE_STEPS.filter(s => s !== 'completed').map((step, idx) => {
              const stepIdx = PIPELINE_STEPS.indexOf(step);
              const isActive = stepIdx === currentStepIndex;
              const isDone = stepIdx < currentStepIndex;
              const StepIcon = STATUS_ICONS[step] || Loader2;
              return (
                <React.Fragment key={step}>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${isDone ? 'bg-green-500/20 text-green-400 border border-green-500/30' : isActive ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 animate-pulse' : 'bg-white/5 text-white/30 border border-white/10'}`}>
                    <StepIcon className={`w-3.5 h-3.5 ${isActive ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">{STATUS_LABELS[step]?.replace('...', '')}</span>
                  </div>
                  {idx < PIPELINE_STEPS.filter(s => s !== 'completed').length - 1 && <div className={`w-4 h-0.5 rounded ${isDone ? 'bg-green-500/50' : 'bg-white/10'}`} />}
                </React.Fragment>
              );
            })}
          </div>
          <p className="text-white/40 text-xs mt-3">Dies kann 1-3 Minuten dauern.</p>
        </div>
      )}

      {/* FERTIGES VIDEO */}
      {currentJob?.status === 'completed' && currentJob.video_url && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3"><Check className="w-5 h-5 text-green-400" /><p className="text-green-400 font-bold">Video fertig!</p></div>
          <div className="rounded-xl overflow-hidden border-2 border-green-500/30">
            <video src={currentJob.video_url} controls autoPlay className="w-full h-auto" playsInline />
          </div>
          <div className="flex gap-2 mt-3">
            <a href={currentJob.video_url} download={`video_${currentJob.lipsync_id || 'output'}.mp4`}
              className="flex-1 py-2.5 px-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 text-sm">
              <Download className="w-4 h-4" /> Herunterladen
            </a>
            <button onClick={async () => { try { const { shareVideo } = await import('../../utils/shareMedia'); const r = await shareVideo({ videoUrl: currentJob.video_url }); if (r === 'clipboard') showSuccessToast('Link kopiert!'); } catch(e){} }}
              className="py-2.5 px-4 bg-white/10 hover:bg-white/20 border-2 border-white/20 text-white font-medium rounded-xl transition-all text-sm flex items-center gap-2">
              <Share2 className="w-4 h-4" /> Teilen
            </button>
            <button onClick={() => { setCurrentJob(null); setText(''); setSelectedImage(null); }}
              className="py-2.5 px-4 bg-indigo-500/20 hover:bg-indigo-500/30 border-2 border-indigo-500/30 text-indigo-300 font-medium rounded-xl transition-all text-sm">
              Neues Video
            </button>
          </div>
        </div>
      )}

      {/* GENERATE BUTTON */}
      {(!isGenerating && currentJob?.status !== 'completed') && (
        <button onClick={handleGenerate} disabled={!selectedImage || !text.trim() || !hasEnoughCredits}
          className={`w-full py-4 px-6 rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-base ${
            !selectedImage || !text.trim() || !hasEnoughCredits
              ? 'bg-white/10 text-white/30 cursor-not-allowed border-2 border-white/10'
              : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg shadow-indigo-500/25'
          }`}>
          <Film className="w-5 h-5" />
          {!hasEnoughCredits ? 'Nicht genug SNAPs (1 benötigt)' : 'Video erstellen — 1 SNAP'}
        </button>
      )}

      {!hasEnoughCredits && !isGenerating && <p className="text-center text-yellow-400/80 text-xs">Du brauchst mindestens 1 SNAP.</p>}
    </div>
  );
};

export default LipSyncPanel;
